import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, AlertCircle, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import { API_BASE } from '../helpers';
import DMVendorPicker from './DMVendorPicker';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_CC = 5;

/**
 * DMCreateEnvelopeModal — create a new draft envelope (POST) or edit an
 * existing draft (PUT). Styling matches the CRM FormModal (.crm-modal*).
 *
 * Props:
 *   mode      — 'create' | 'edit'
 *   envelope  — for edit mode: { envelope, recipients } detail to prefill
 *   onClose   — () => void
 *   onSuccess — (detailResponse) => void   (full detail payload from API)
 */
function DMCreateEnvelopeModal({ mode = 'create', envelope, onClose, onSuccess }) {
  const isEdit = mode === 'edit';
  const env = envelope?.envelope;
  const existingRecipients = envelope?.recipients || [];
  const signer = existingRecipients.find(r => r.role === 'signer');
  // An ad-hoc signer is snapshotted with no CRM ContactId.
  const signerIsAdhoc = !!signer && signer.contactId == null;

  const [categories, setCategories] = useState([]);
  const [recipientMode, setRecipientMode] = useState(signerIsAdhoc ? 'adhoc' : 'vendor');
  const [adhocName, setAdhocName] = useState(signerIsAdhoc ? (signer.name || '') : '');
  const [adhocEmail, setAdhocEmail] = useState(signerIsAdhoc ? (signer.email || '') : '');
  const [form, setForm] = useState(() => ({
    documentCategory: env?.documentCategory || '',
    message: env?.message || '',
    propertyAddress: env?.propertyAddress || '',
    unitIdentifier: env?.unitIdentifier || '',
    jobReference: env?.jobReference || '',
    jobDate: env?.jobDate ? String(env.jobDate).split('T')[0] : '',
    internalNotes: env?.internalNotes || '',
  }));
  const [vendor, setVendor] = useState(
    signer && !signerIsAdhoc
      ? { contactId: signer.contactId, name: signer.name, email: signer.email, contactType: signer.contactType || null }
      : null
  );
  const [ccRows, setCcRows] = useState(() => {
    const ccs = existingRecipients.filter(r => r.role === 'cc');
    return ccs.length > 0 ? ccs.map(c => ({ name: c.name || '', email: c.email || '' })) : [];
  });
  const [provider, setProvider] = useState(() => env?.provider || 'internal');
  const [showJobContext, setShowJobContext] = useState(() => {
    return !!(env && (env.propertyAddress || env.unitIdentifier || env.jobReference || env.jobDate || env.internalNotes));
  });
  const [banner, setBanner] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Bug B: flips true on the first user-driven edit to any form field;
  // reset on successful submit. Gates the discard-confirm on every close.
  const [dirty, setDirty] = useState(false);
  // Bug A: true only when a backdrop press ORIGINATED on the backdrop. A
  // text-selection drag that starts inside an input and releases on the
  // backdrop must not be treated as a click-outside dismiss.
  const overlayPressOnBackdrop = useRef(false);

  useEffect(() => {
    axios.get(`${API_BASE}/dm/options/document-categories`)
      .then(res => setCategories(res.data || []))
      .catch(() => setCategories([]));
  }, []);

  const update = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  const addCcRow = () => {
    if (ccRows.length >= MAX_CC) return;
    setCcRows(prev => [...prev, { name: '', email: '' }]);
    setDirty(true);
  };
  const updateCcRow = (i, field, val) =>
    setCcRows(prev => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeCcRow = (i) => {
    setCcRows(prev => prev.filter((_, idx) => idx !== i));
    setDirty(true);
  };

  const validate = () => {
    if (!form.documentCategory) return 'Document category is required.';
    if (recipientMode === 'adhoc') {
      if (!adhocName.trim()) return 'Recipient name is required.';
      if (adhocName.trim().length > 200) return 'Recipient name must be 200 characters or fewer.';
      if (!EMAIL_RE.test(adhocEmail.trim())) return 'Please enter a valid recipient email.';
    } else if (!vendor || !vendor.contactId) {
      return 'Please select a contact.';
    }
    for (const row of ccRows) {
      const name = row.name.trim();
      const email = row.email.trim();
      if (!name && !email) continue; // empty row, will be dropped
      if (!name) return 'Each CC recipient needs a name.';
      if (!EMAIL_RE.test(email)) return 'Please enter a valid email for each CC recipient.';
    }
    if (form.jobDate && isNaN(Date.parse(form.jobDate))) return 'Job date must be a valid date.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const clientError = validate();
    if (clientError) {
      setBanner(clientError);
      return;
    }
    setBanner(null);
    setSubmitting(true);

    const ccRecipients = ccRows
      .map(r => ({ name: r.name.trim(), email: r.email.trim() }))
      .filter(r => r.name || r.email);

    const body = {
      // No title — the backend derives Title from DocumentCategory.
      message: form.message.trim() || undefined,
      documentCategory: form.documentCategory,
      provider,  // 'internal' or 'dropbox-sign'
      ...(recipientMode === 'adhoc'
        ? { adhocSigner: { name: adhocName.trim(), email: adhocEmail.trim() } }
        : { vendorContactId: Number(vendor.contactId) }),
      ccRecipients,
      propertyAddress: form.propertyAddress.trim() || undefined,
      unitIdentifier: form.unitIdentifier.trim() || undefined,
      jobReference: form.jobReference.trim() || undefined,
      jobDate: form.jobDate || undefined,
      internalNotes: form.internalNotes.trim() || undefined,
    };

    try {
      const res = isEdit
        ? await axios.put(`${API_BASE}/dm/envelopes/${env.envelopeId}`, body)
        : await axios.post(`${API_BASE}/dm/envelopes`, body);
      setDirty(false); // submit succeeded — closing now must never prompt
      onSuccess(res.data);
    } catch (err) {
      const data = err.response?.data;
      const detailMsg = Array.isArray(data?.details) && data.details.length
        ? data.details.join(' ')
        : data?.error;
      if (err.response?.status === 400 || err.response?.status === 409) {
        setBanner(detailMsg || 'Please check the form and try again.');
      } else {
        setBanner(isEdit
          ? 'Could not save changes. Please try again.'
          : 'Could not create envelope. Please try again.');
      }
      setSubmitting(false);
    }
  };

  // Bug B: never discard in-progress work without warning. A clean form
  // (dirty === false) still closes silently, exactly as before.
  const requestClose = () => {
    if (dirty) {
      const msg = isEdit
        ? "Discard your unsaved changes? You'll lose any edits since the last save."
        : 'Discard this draft envelope? Your changes will be lost.';
      if (!window.confirm(msg)) return;
    }
    onClose();
  };

  // Close on Escape, through the same dirty guard as every other close path.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, isEdit, onClose]);

  // Bug A: the backdrop sets this true only when the press lands on the
  // backdrop ITSELF; a press on any child (an input being selected) leaves
  // it false. The synthesized click from a drag that began inside the
  // modal is then ignored, so the modal stays open.
  const handleOverlayMouseDown = (e) => {
    overlayPressOnBackdrop.current = e.target === e.currentTarget;
  };
  const handleOverlayClick = (e) => {
    if (e.target !== e.currentTarget) return;     // click resolved to a child
    if (!overlayPressOnBackdrop.current) return;  // press began inside the modal
    overlayPressOnBackdrop.current = false;
    requestClose();
  };

  return (
    <div
      className="crm-modal__overlay"
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayClick}
    >
      <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal__header">
          <h2 className="crm-modal__title">{isEdit ? 'Edit Draft Envelope' : 'New Envelope'}</h2>
          <button onClick={requestClose} className="crm-btn crm-btn--secondary crm-btn--icon" data-tooltip="Close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} onChange={() => setDirty(true)}>
          <div className="crm-modal__body">
            {banner && (
              <div className="dm-banner-error">
                <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{banner}</span>
              </div>
            )}

            {/* Section: Document */}
            <div className="dm-section-group">
              <h3 className="dm-section-group__title">Document</h3>
              {/* No Title field — the selected Category serves as the
                  envelope title (backend defaults Title = DocumentCategory). */}
              <div className="crm-form__group">
                <label className="crm-form__label">Document Category *</label>
                <select
                  className="crm-form__select"
                  value={form.documentCategory}
                  onChange={(e) => update('documentCategory', e.target.value)}
                >
                  <option value="">Select a category...</option>
                  {categories.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="crm-form__group">
                <label className="crm-form__label">Message to recipient</label>
                <textarea
                  className="crm-form__textarea"
                  rows={3}
                  value={form.message}
                  onChange={(e) => update('message', e.target.value)}
                />
                <p className="dm-hint">This message appears in the email the recipient receives.</p>
              </div>
            </div>

            {/* Section: Signature Type */}
            <div className="dm-section-group">
              <h3 className="dm-section-group__title">Signature Type</h3>
              <div className="crm-form__group">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="provider"
                      value="internal"
                      checked={provider === 'internal'}
                      onChange={() => setProvider('internal')}
                      style={{ marginTop: '3px' }}
                    />
                    <span>
                      <strong>Basic signature</strong> — in-app, free
                      <div className="dm-hint" style={{ marginTop: 2 }}>
                        The signer receives a secure link from our app and signs in your browser. Best for non-contract documents like acknowledgments, releases, or simple consents.
                      </div>
                    </span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="provider"
                      value="dropbox-sign"
                      checked={provider === 'dropbox-sign'}
                      onChange={() => setProvider('dropbox-sign')}
                      style={{ marginTop: '3px' }}
                    />
                    <span>
                      <strong>Certified signature</strong> — Dropbox Sign
                      <div className="dm-hint" style={{ marginTop: 2 }}>
                        Sent through Dropbox Sign for legally binding contracts with a third-party audit trail. Recommended for contracts, leases, and anything requiring strong legal weight.
                      </div>
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Section: Recipient */}
            <div className="dm-section-group">
              <h3 className="dm-section-group__title">Recipient</h3>
              <div className="crm-form__group">
                <label className="crm-form__label">Recipient *</label>
                <div style={{ display: 'flex', gap: '1.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem' }}>
                    <input
                      type="radio"
                      name="recipientMode"
                      checked={recipientMode === 'vendor'}
                      onChange={() => setRecipientMode('vendor')}
                    />
                    Select from Contacts
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem' }}>
                    <input
                      type="radio"
                      name="recipientMode"
                      checked={recipientMode === 'adhoc'}
                      onChange={() => setRecipientMode('adhoc')}
                    />
                    Send to ad-hoc recipient
                  </label>
                </div>
              </div>
              {recipientMode === 'vendor' ? (
                <div className="crm-form__group">
                  <label className="crm-form__label">Contact *</label>
                  <DMVendorPicker value={vendor} onChange={(v) => { setVendor(v); setDirty(true); }} />
                </div>
              ) : (
                <>
                  <div className="crm-form__group">
                    <label className="crm-form__label">Recipient Name *</label>
                    <input
                      type="text"
                      className="crm-form__input"
                      maxLength={200}
                      value={adhocName}
                      onChange={(e) => setAdhocName(e.target.value)}
                    />
                  </div>
                  <div className="crm-form__group">
                    <label className="crm-form__label">Recipient Email *</label>
                    <input
                      type="email"
                      className="crm-form__input"
                      value={adhocEmail}
                      onChange={(e) => setAdhocEmail(e.target.value)}
                    />
                  </div>
                </>
              )}
              <div className="crm-form__group">
                <label className="crm-form__label">CC recipients</label>
                {ccRows.map((row, i) => (
                  <div className="dm-cc-row" key={i}>
                    <input
                      type="text"
                      className="crm-form__input"
                      placeholder="Name"
                      value={row.name}
                      onChange={(e) => updateCcRow(i, 'name', e.target.value)}
                    />
                    <input
                      type="email"
                      className="crm-form__input"
                      placeholder="Email"
                      value={row.email}
                      onChange={(e) => updateCcRow(i, 'email', e.target.value)}
                    />
                    <button
                      type="button"
                      className="crm-btn crm-btn--secondary crm-btn--icon"
                      onClick={() => removeCcRow(i)}
                      data-tooltip="Remove"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                {ccRows.length < MAX_CC && (
                  <button type="button" className="crm-btn crm-btn--secondary" onClick={addCcRow}>
                    <Plus size={16} /> Add CC
                  </button>
                )}
              </div>
            </div>

            {/* Section: Job Context (collapsible, collapsed by default) */}
            <button
              type="button"
              className="dm-collapse-toggle"
              onClick={() => setShowJobContext(s => !s)}
            >
              {showJobContext ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              Job Context (optional)
            </button>
            {showJobContext && (
              <div className="dm-section-group">
                <div className="crm-form__group">
                  <label className="crm-form__label">Property address</label>
                  <input
                    type="text"
                    className="crm-form__input"
                    value={form.propertyAddress}
                    onChange={(e) => update('propertyAddress', e.target.value)}
                  />
                </div>
                <div className="crm-form__row">
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Unit identifier</label>
                    <input
                      type="text"
                      className="crm-form__input"
                      placeholder="Unit 2B"
                      value={form.unitIdentifier}
                      onChange={(e) => update('unitIdentifier', e.target.value)}
                    />
                  </div>
                  <div className="crm-form__group crm-form__col">
                    <label className="crm-form__label">Job reference</label>
                    <input
                      type="text"
                      className="crm-form__input"
                      placeholder="Work order number"
                      value={form.jobReference}
                      onChange={(e) => update('jobReference', e.target.value)}
                    />
                  </div>
                </div>
                <div className="crm-form__group">
                  <label className="crm-form__label">Job date</label>
                  <input
                    type="date"
                    className="crm-form__input"
                    value={form.jobDate}
                    onChange={(e) => update('jobDate', e.target.value)}
                  />
                </div>
                <div className="crm-form__group">
                  <label className="crm-form__label">Internal notes</label>
                  <textarea
                    className="crm-form__textarea"
                    rows={3}
                    value={form.internalNotes}
                    onChange={(e) => update('internalNotes', e.target.value)}
                  />
                  <p className="dm-hint">
                    These notes are only visible to Cagan staff and are never sent to the recipient.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="crm-modal__footer">
            <button type="button" onClick={requestClose} className="crm-btn crm-btn--secondary">
              Cancel
            </button>
            <button type="submit" className="crm-btn dm-btn--gold" disabled={submitting}>
              {submitting && <span className="dm-inline-spinner" />}
              {isEdit ? 'Save Changes' : 'Create Draft'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DMCreateEnvelopeModal;
